import { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import modelAutocompleteFormatter from "../../actprep-ui/utils/modelAutocompleteFormatters";
import LoadingButton from "@mui/lab/LoadingButton";
import api, { serializeSearchParams } from "../../actprep-ui/utils/api";
import { range } from "../../actprep-ui/utils/helpers";
import { apiUrls, urls } from "../../actprep-ui/utils/urls";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import {
  Badge,
  Chip,
  List,
  ListItemButton,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useGet } from "../../actprep-ui/hooks/api";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import ImageDialog from "../../actprep-ui/components/ImageDialog";
import {
  Edit,
  Image,
  PersonAdd,
  Delete,
  Edit as EditIcon,
  ExpandMore,
} from "@mui/icons-material";
import { fullName } from "../../actprep-ui/utils/formatting";
import PhotoEditDialog from "../../actprep-ui/components/PhotoEditDialog/";
import ProfilePictureUpload from "../../actprep-ui/components/ProfilePictureUpload";
import AssignStudentToCoachForm from "../AssignStudentToCoachForm";
import CreateProgramChatDialog from "./CreateProgramChatDialog";
import { confirm } from "material-ui-confirm";
import dayjs from "dayjs";
import { PARENT_APP_ORIGIN, TESTING_APP_ORIGIN } from "../../constants/misc";

function serializeFormData(formData) {
  const serializedFormData = {};
  for (let [key, val] of Object.entries(formData)) {
    if (typeof val == "object" && !Array.isArray(val)) {
      val = val?.uuid || null;
    }
    serializedFormData[key] = val;
  }
  return serializedFormData;
}

function createFormData(studentProfile) {
  return {
    email: studentProfile.email,
    cohort_id: modelAutocompleteFormatter("Cohort", studentProfile.cohort),
    organization_id: modelAutocompleteFormatter(
      "Organization",
      studentProfile.organization
    ),
    goal_score: studentProfile.goal_score || null,
    is_processed: studentProfile.is_processed || false,
    whatsapp_group_created: studentProfile.whatsapp_group_created || false,
    added_to_group_chat: studentProfile.added_to_group_chat || false,
    supplies_shipped: studentProfile.supplies_shipped || false,
    attending_in_person: studentProfile.attending_in_person || false,
    registered_for_official_act:
      studentProfile.registered_for_official_act || false,
    registered_for_mock_act: studentProfile.registered_for_mock_act || false,
    memorization_text_sent: studentProfile.memorization_text_sent || false,
  };
}
export default function StudentProfileForm({ studentProfile, onSubmit }) {
  const [formData, setFormData] = useState(createFormData(studentProfile));
  const [isLoading, setIsLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [chatLoading, setChatLoading] = useState({
    parent: false,
    coaching: false,
  });
  const [chatDialog, setChatDialog] = useState(null);
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [chatStatus, setChatStatus] = useState({
    parent_chat_created: !!studentProfile.parent_chat_created,
    coaching_chat_created: !!studentProfile.coaching_chat_created,
  });

  const { data: studentPhotoData, refetch: refetchAdditionalData } = useGet(
    apiUrls.admin.studentProfiles(studentProfile.uuid),
    {
      params: {
        values: [
          "wall_of_greats_image",
          "social_media_image",
          "uuid",
          "user__profile__profile_pic",
        ],
        selectRelated: ["user", "user__profile"],
      },
    }
  );

  const userProfile = studentPhotoData?.data?.user?.profile;
  const studentInitials = `${studentProfile.first_name?.[0] || ""}${
    studentProfile.last_name?.[0] || ""
  }`;

  const { data: assignmentsData, refetch: refetchAssignments } = useGet(
    apiUrls.coaching.studentToCoachLink(),
    {
      params: {
        search_params: {
          student_id: studentProfile.uuid,
        },
        keep_related: true,
        values: [
          "uuid",
          "role_assignment",
          "coach",
          "is_anchor",
          "anchor__uuid",
          "start_date",
          "end_date",
          "is_active",
        ],
        exclude: ["coach__user", "role_assignment__coach"],
        order_by: ["start_date"],
      },
    }
  );

  const additionalStudentData = studentPhotoData?.data || {};
  const viewWogPhotoDisc = useDisclosure();
  const editWogPhotoDisc = useDisclosure();
  const viewSocialMediaPhotoDisc = useDisclosure();
  const editSocialMediaPhotoDisc = useDisclosure();
  const createProfilePicDisc = useDisclosure();
  const assignToCoachDisc = useDisclosure();
  const editAssignmentDisc = useDisclosure();
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const updateFormValue = (field, newValue) => {
    if (!isChanged) {
      setIsChanged(true);
    }
    setFormData((formData) => ({
      ...formData,
      [field]: newValue,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const data = serializeFormData(formData);
    const response = await api.put(
      apiUrls.admin.studentProfiles(studentProfile.uuid),
      data
    );
    if (!response.status == 200) {
      return;
    }
    setIsLoading(false);
    setIsChanged(false);
    showSuccessSnackbar("Student profile saved!");
    if (onSubmit) {
      onSubmit(response.data);
    }
  };

  useEffect(() => {
    setFormData(createFormData(studentProfile));
    setChatStatus({
      parent_chat_created: !!studentProfile.parent_chat_created,
      coaching_chat_created: !!studentProfile.coaching_chat_created,
    });
  }, [studentProfile]);

  const handleReset = () => {
    setFormData(createFormData(studentProfile));
    setIsChanged(false);
  };

  const handleAssignmentSubmit = (assignmentData) => {
    refetchAssignments();
    if (onSubmit) {
      onSubmit(assignmentData);
    }
  };

  const handleEditAssignment = (assignment) => {
    const roleAssignment = assignment.role_assignment;
    setSelectedAssignment({
      ...assignment,
      role_assignment: {
        uuid: roleAssignment.uuid,
        label: `${fullName(assignment.coach?.user_profile.user)} as ${
          roleAssignment?.role
        } [${assignment.start_date} - ${assignment.end_date ?? "Ongoing"}]`,
        data: {
          start_date: roleAssignment.start_date,
          end_date: roleAssignment.end_date,
        },
      },
    });
    editAssignmentDisc.onOpen();
  };

  const handleDeleteAssignment = async (assignment) => {
    confirm({
      title: "Delete Coach Assignment",
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete this assignment for{" "}
            <strong>{fullName(studentProfile)}</strong>?
          </Typography>
          {renderAssignmentCard(assignment, 0, true)}
        </Box>
      ),
      confirmationText: "Delete",
      cancellationText: "Cancel",
      confirmationButtonProps: {
        color: "error",
        variant: "contained",
      },
    })
      .then(async () => {
        try {
          const response = await api.delete(
            apiUrls.coaching.studentToCoachLink(assignment.uuid)
          );

          if (response.data.success === false) {
            showErrorSnackbar(
              response.data.reason || "Failed to delete assignment"
            );
            return;
          }

          showSuccessSnackbar("Assignment deleted successfully!");
          refetchAssignments();

          if (onSubmit) {
            onSubmit(response.data);
          }
        } catch (error) {
          console.error("Error deleting assignment:", error);
          const errorMessage =
            error.response?.data?.reason || "Failed to delete assignment";
          showErrorSnackbar(errorMessage);
        }
      })
      .catch(() => {
        // User cancelled
      });
  };

  const handleCreateChat = async (chatType) => {
    const titlePrefix = chatType === "coaching" ? "Coaching" : "Parent";
    setChatLoading((state) => ({ ...state, [chatType]: true }));

    try {
      const previewResponse = await api.get(
        apiUrls.admin.studentChats(studentProfile.uuid),
        {
          params: serializeSearchParams({
            search_params: { chat_type: chatType },
          }),
        }
      );
      const previewData = previewResponse?.data?.data || {};
      if (!previewData.can_create) {
        showErrorSnackbar(
          previewData.reason ||
            `Unable to create ${titlePrefix.toLowerCase()} chat.`
        );
        return;
      }

      const fallbackName = `${fullName(studentProfile)} ${titlePrefix} Chat`.trim();
      setChatDialog({
        chatType,
        suggestedName: previewData.suggested_name || fallbackName,
        members: previewData.members || [],
      });
    } catch (error) {
      showErrorSnackbar(
        error?.response?.data?.reason ||
          `Failed to create ${titlePrefix.toLowerCase()} chat.`
      );
    } finally {
      setChatLoading((state) => ({ ...state, [chatType]: false }));
    }
  };

  const handleConfirmCreateChat = async ({ name, memberIds }) => {
    if (!chatDialog) {
      return;
    }
    const chatType = chatDialog.chatType;
    const titlePrefix = chatType === "coaching" ? "Coaching" : "Parent";
    setChatSubmitting(true);
    try {
      const response = await api.post(
        apiUrls.admin.studentChats(studentProfile.uuid),
        {
          chat_type: chatType,
          name,
          member_ids: memberIds,
        }
      );
      if (response?.data?.success === false) {
        showErrorSnackbar(
          response?.data?.reason ||
            `Failed to create ${titlePrefix.toLowerCase()} chat.`
        );
        return;
      }

      const payload = response?.data?.data || {};
      const updates = payload.student_updates || {};
      setChatStatus((state) => ({ ...state, ...updates }));

      if (onSubmit && Object.keys(updates).length > 0) {
        onSubmit(updates);
      }

      setChatDialog(null);
      showSuccessSnackbar(
        payload.created
          ? `${titlePrefix} chat created.`
          : `${titlePrefix} chat already exists with matching members.`
      );
    } catch (error) {
      showErrorSnackbar(
        error?.response?.data?.reason ||
          `Failed to create ${titlePrefix.toLowerCase()} chat.`
      );
    } finally {
      setChatSubmitting(false);
    }
  };

  const confirmationList = [
    ["whatsapp_group_created", "Whatsapp group created"],
    ["added_to_group_chat", "Added to chat"],
    ["attending_in_person", "Attending in-person"],
    ["supplies_shipped", "Supplies Shipped"],
    ["registered_for_official_act", "Registered for Official ACT"],
    ["registered_for_mock_act", "Registered for Mock ACT"],
    ["memorization_text_sent", "Memorization Text Sent"],
    ["is_processed", "Fully Processed"],
  ];
  const confirmationsShown = Object.fromEntries(
    confirmationList.map(([confirmation]) => {
      if (
        confirmation == "attending_in_person" &&
        !studentProfile.most_recent_ticket_seat_type
          ?.toLowerCase()
          .includes("physical")
      ) {
        return [confirmation, false];
      } else if (
        confirmation == "supplies_shipped" &&
        !(
          studentProfile.most_recent_ticket_seat_type
            ?.toLowerCase()
            .includes("online") || !formData.attending_in_person
        )
      ) {
        return [confirmation, false];
      }
      return [confirmation, true];
    })
  );

  // Group assignments by anchor
  const groupedAssignments = useMemo(() => {
    if (!assignmentsData?.data) return { anchors: [], nonAnchors: [] };

    const anchors = assignmentsData.data.filter(
      (assignment) => assignment.is_anchor
    );
    const nonAnchors = assignmentsData.data.filter(
      (assignment) => !assignment.is_anchor
    );

    // Group non-anchors by their anchor
    const groupedNonAnchors = nonAnchors.reduce((acc, assignment) => {
      const anchorUuid = assignment.anchor.uuid;
      if (!acc[anchorUuid]) {
        acc[anchorUuid] = [];
      }
      acc[anchorUuid].push(assignment);
      return acc;
    }, {});

    return { anchors, groupedNonAnchors };
  }, [assignmentsData?.data]);

  const renderAssignmentCard = (
    assignment,
    totalRelated,
    disableActions = false
  ) => (
    <Card
      sx={{
        width: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      }}
      variant={assignment.is_anchor ? "elevation" : "outlined"}
      elevation={0}
    >
      <CardContent sx={{ p: 0.5, "&:last-child": { pb: 0.5 } }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Avatar
            sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
            src={assignment.coach?.user_profile?.profile_pic}
          >
            {assignment.coach?.user_profile?.user?.first_name?.[0]}
            {assignment.coach?.user_profile?.user?.last_name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                fontWeight="medium"
                noWrap
                sx={{ fontSize: "0.875rem" }}
              >
                {assignment.coach?.user_profile?.user?.first_name}{" "}
                {assignment.coach?.user_profile?.user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {assignment.role_assignment?.role}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {dayjs(assignment.start_date).format("MMM D, YYYY")} -{" "}
              {assignment.end_date
                ? dayjs(assignment.end_date).format("MMM D, YYYY")
                : "Ongoing"}
            </Typography>
          </Box>
          {!disableActions && (
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditAssignment(assignment);
                }}
                title="Edit Assignment"
                sx={{
                  width: 30,
                  height: 30,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAssignment(assignment);
                }}
                title="Delete Assignment"
                color="error"
                sx={{
                  width: 30,
                  height: 30,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>
          )}
        </Stack>
        {totalRelated > 0 && (
          <Chip
            label={`+${totalRelated} related`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              m: 1,
              fontSize: "0.7rem",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                transform: "scale(1.05)",
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
  return (
    <>
      {additionalStudentData?.wall_of_greats_image && (
        <ImageDialog
          src={additionalStudentData.wall_of_greats_image}
          {...viewWogPhotoDisc.getDisclosureProps()}
        />
      )}
      <PhotoEditDialog
        currentImage={additionalStudentData.wall_of_greats_image}
        title={
          <>
            Editing {fullName(studentProfile)}
            {"'"}s Wall of Greats Photo
          </>
        }
        onSubmit={async (newImage) => {
          const formData = new FormData();
          formData.append("image_field", "wall_of_greats_image");
          formData.append("image", newImage);
          const response = await api.post(
            apiUrls.admin.imageUpload(studentProfile.uuid),
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          if (response.status == 200) {
            showSuccessSnackbar("Photo Updated!");
            if (onSubmit) {
              onSubmit({
                ...studentProfile,
                wall_of_greats_image_uploaded: true,
              });
            }
            editWogPhotoDisc.onClose();
            refetchAdditionalData();
          }
        }}
        {...editWogPhotoDisc.getDisclosureProps()}
      />
      <ProfilePictureUpload
        isOpen={createProfilePicDisc.isOpen}
        onClose={createProfilePicDisc.onClose}
        initialImage={additionalStudentData.wall_of_greats_image}
        title={`Create Profile Picture for ${fullName(studentProfile)}`}
        fieldName="image"
        onSubmit={async (imageBlob) => {
          const formData = new FormData();
          formData.append("image_field", "profile_pic");
          formData.append("image", imageBlob, "profile_pic.png");
          const response = await api.post(
            apiUrls.admin.imageUpload(studentProfile.uuid),
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          if (response.status == 200) {
            showSuccessSnackbar("Profile Picture Created!");
            if (onSubmit) {
              onSubmit({
                ...studentProfile,
                profile_pic_uploaded: true,
              });
            }
            refetchAdditionalData();
          }
        }}
      />
      {additionalStudentData?.social_media_image && (
        <ImageDialog
          src={additionalStudentData.social_media_image}
          {...viewSocialMediaPhotoDisc.getDisclosureProps()}
        />
      )}
      <PhotoEditDialog
        currentImage={additionalStudentData.social_media_image}
        title={
          <>
            Editing {fullName(studentProfile)}
            {"'"}s Social Media Photo
          </>
        }
        onSubmit={async (newImage) => {
          const formData = new FormData();
          formData.append("image_field", "social_media_image");
          formData.append("image", newImage);
          const response = await api.post(
            apiUrls.admin.imageUpload(studentProfile.uuid),
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          if (response.status == 200) {
            showSuccessSnackbar("Photo Updated!");
            if (onSubmit) {
              onSubmit({
                ...studentProfile,
                social_media_image_uploaded: true,
              });
            }
            editSocialMediaPhotoDisc.onClose();
            refetchAdditionalData();
          }
        }}
        {...editSocialMediaPhotoDisc.getDisclosureProps()}
      />
      <AssignStudentToCoachForm
        studentProfile={studentProfile}
        isOpen={assignToCoachDisc.isOpen}
        onClose={assignToCoachDisc.onClose}
        onSubmit={handleAssignmentSubmit}
      />
      <AssignStudentToCoachForm
        studentProfile={studentProfile}
        assignment={selectedAssignment}
        isOpen={editAssignmentDisc.isOpen}
        onClose={() => {
          editAssignmentDisc.onClose();
          setSelectedAssignment(null);
        }}
        onSubmit={handleAssignmentSubmit}
      />
      <Box component="form" onSubmit={handleSubmit}>
        <Box mb={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={userProfile?.profile_pic || undefined}
              sx={{ width: 36, height: 36 }}
            >
              {studentInitials}
            </Avatar>
            <Typography variant="h6">{fullName(studentProfile)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            <Chip
              label="Student Dashboard"
              size="small"
              onClick={() =>
                window.open(
                  TESTING_APP_ORIGIN +
                    urls.students.impersonate(studentProfile?.uuid),
                  "_blank"
                )
              }
            />
            <Chip
              label="Parent Dashboard"
              size="small"
              onClick={() =>
                window.open(
                  `${PARENT_APP_ORIGIN}/impersonate/${studentProfile?.parent?.uuid}/`,
                  "_blank"
                )
              }
            />
          </Stack>
        </Box>
        <Box mb={2}>
          <Typography variant="body2">Wall of Greats Photo</Typography>
          {additionalStudentData?.wall_of_greats_image && (
            <Chip
              label="View Photo"
              size="small"
              icon={<Image />}
              {...viewWogPhotoDisc.getButtonProps()}
              sx={{ mr: 1 }}
            />
          )}
          <Chip
            label={
              additionalStudentData?.wall_of_greats_image
                ? "Edit Photo"
                : "Upload Photo"
            }
            size="small"
            icon={<Edit />}
            {...editWogPhotoDisc.getButtonProps()}
          />
        </Box>
        {additionalStudentData?.wall_of_greats_image && (
          <Box mb={2}>
            <Typography variant="body2">Profile Picture</Typography>
            <Chip
              label="Create Profile Picture"
              size="small"
              icon={<Edit />}
              color="primary"
              {...createProfilePicDisc.getButtonProps()}
            />
          </Box>
        )}
        <Box mb={2}>
          <Typography variant="body2">Social Media Photo</Typography>
          {additionalStudentData?.social_media_image && (
            <Chip
              label="View Photo"
              size="small"
              icon={<Image />}
              {...viewSocialMediaPhotoDisc.getButtonProps()}
              sx={{ mr: 1 }}
            />
          )}
          <Chip
            label={
              additionalStudentData?.social_media_image
                ? "Edit Photo"
                : "Upload Photo"
            }
            size="small"
            icon={<Edit />}
            {...editSocialMediaPhotoDisc.getButtonProps()}
          />
        </Box>
        <Box mb={2}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              Coach Assignments ({groupedAssignments.anchors.length})
            </Typography>
            <IconButton size="small" {...assignToCoachDisc.getButtonProps()}>
              <PersonAdd fontSize="small" />
            </IconButton>
          </Box>

          {/* Current Assignments List */}
          {assignmentsData?.data && assignmentsData.data.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack spacing={1}>
                {groupedAssignments.anchors.map((anchorAssignment) => {
                  const relatedAssignments =
                    groupedAssignments.groupedNonAnchors[
                      anchorAssignment.uuid
                    ] || [];
                  const totalRelated = relatedAssignments.length;

                  return (
                    <Accordion
                      key={anchorAssignment.uuid}
                      defaultExpanded={false}
                      variant="outlined"
                      disableGutters
                      sx={{
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          minHeight: "auto",
                          "& .MuiAccordionSummary-content": {
                            margin: "8px 0",
                          },
                        }}
                        component="div"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                          }}
                        >
                          {renderAssignmentCard(anchorAssignment, totalRelated)}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Stack spacing={1}>
                          {relatedAssignments.map((assignment) => (
                            <Box key={assignment.uuid}>
                              {renderAssignmentCard(assignment, 0, true)}
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
        <Box mb={2}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Program Chats
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <LoadingButton
              size="small"
              variant="outlined"
              loading={chatLoading.parent}
              disabled={chatStatus.parent_chat_created}
              onClick={() => handleCreateChat("parent")}
            >
              {chatStatus.parent_chat_created
                ? "Parent Chat Created"
                : "Create Parent Chat"}
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="outlined"
              loading={chatLoading.coaching}
              disabled={chatStatus.coaching_chat_created}
              onClick={() => handleCreateChat("coaching")}
            >
              {chatStatus.coaching_chat_created
                ? "Coaching Chat Created"
                : "Create Coaching Chat"}
            </LoadingButton>
          </Stack>
        </Box>
        <CreateProgramChatDialog
          open={Boolean(chatDialog)}
          chatType={chatDialog?.chatType}
          suggestedName={chatDialog?.suggestedName || ""}
          lockedMembers={chatDialog?.members || []}
          submitting={chatSubmitting}
          onClose={() => {
            if (!chatSubmitting) {
              setChatDialog(null);
            }
          }}
          onCreate={handleConfirmCreateChat}
        />
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label={"Email"}
              variant="standard"
              value={formData.email}
              disabled={!!studentProfile.user}
              fullWidth
              required
              onChange={(event) => updateFormValue("email", event.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <ModelAutoComplete
              modelName={"Organization"}
              label={"Organization"}
              variant="standard"
              value={formData.organization_id}
              setValue={(newVal) => updateFormValue("organization_id", newVal)}
            />
          </Grid>
          <Grid size={12}>
            <ModelAutoComplete
              modelName={"Cohort"}
              label={"Cohort"}
              variant="standard"
              value={formData.cohort_id}
              setValue={(newVal) => updateFormValue("cohort_id", newVal)}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              value={formData.goal_score}
              options={range(1, 36)}
              getOptionLabel={(option) => option.toString()}
              renderInput={(params) => (
                <TextField {...params} label="Goal Score" variant="standard" />
              )}
              onChange={(_, newValue) =>
                updateFormValue("goal_score", newValue)
              }
            />
          </Grid>
          <Grid size={12}>
            <Badge
              badgeContent={
                confirmationList.filter(
                  ([confirmation]) =>
                    !formData[confirmation] &&
                    confirmationsShown[confirmation] &&
                    confirmation != "attending_in_person"
                ).length
              }
              sx={{
                "& .MuiBadge-badge": {
                  right: -12,
                  top: 13,
                  padding: "0 4px",
                },
              }}
              color="error"
            >
              <Typography variant="overline">Confirmations</Typography>
            </Badge>
            <List sx={{ maxHeight: "15rem", overflowY: "auto" }}>
              {confirmationList
                .filter(([confirmation]) => confirmationsShown[confirmation])
                .map(([confirmation, confirmationLabel]) => {
                  return (
                    <ListItemButton
                      key={confirmation}
                      onClick={() =>
                        updateFormValue(confirmation, !formData[confirmation])
                      }
                    >
                      <Switch
                        checked={formData[confirmation]}
                        color="secondary"
                      />
                      <Typography variant="body1">
                        {confirmationLabel}
                      </Typography>
                    </ListItemButton>
                  );
                })}
            </List>
          </Grid>
          <Grid size={12}>
            <ButtonGroup fullWidth disableElevation>
              <LoadingButton onClick={handleReset} disabled={!isChanged}>
                Reset
              </LoadingButton>
              <LoadingButton
                type="submit"
                loading={isLoading}
                disabled={!isChanged}
              >
                Save
              </LoadingButton>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
